import { PageContainer, PageHeader, Text, VStack } from '@/components/Atoms'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/Card'
import { useCrtCurrencySync } from '@/hooks/crt/useCrtCurrencySync'
import {
  isDrillableVarianceType,
  resolveComparison,
  useVarianceBreakdown,
  useVarianceTotals,
  varianceAnalysisSearchSchema,
  VarianceQuickFilter,
} from '@/hooks/queries/crt/useVarianceAnalysis'
import {
  extractCustomZodIssues,
  getTranslatedCustomIssueDescription,
} from '@/lib/utils/zodErrorHelpers'
import {
  DEFAULT_VARIANCE_MEASURE,
  DEFAULT_VARIANCE_TYPE,
  VARIANCE_TYPE_UNITS,
  VARIANCE_UNITS,
  type SelectableVarianceType,
} from '@repo/constants'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ComparisonConfigurator, type ComparisonPatch } from './-components/ComparisonConfigurator'
import {
  QUICK_FILTER_SCOPE_KEYS,
  QuickFilterBar,
  VARIANCE_QUICK_FILTER_SLICES,
} from './-components/QuickFilterBar'
import { VarianceHeatmap } from './-components/VarianceHeatmap'
import { DEFAULT_VARIANCE_QUICK_FILTER, resolveVarianceScope } from './-data/resolveVarianceScope'

import { VarianceTotalsCards } from './-components/VarianceTotalsCards'
import { VarianceZeroNotes } from './-components/VarianceZeroNotes'

export const Route = createFileRoute('/_auth/_modules/crt/variance-analysis/')({
  component: RouteComponent,
  validateSearch: (search) => varianceAnalysisSearchSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation(['crt', 'common'])
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  // The cards and the table only exist once both sides are fully configured and Compare has written them
  // to the URL; until then the page is just the configurator.
  const comparison = resolveComparison(search)

  // Both bars have a default rather than an "all" state: every breakdown is keyed on a product category,
  // and the table's single value column has to report *some* measure.
  const quickFilter = search.quickFilter ?? DEFAULT_VARIANCE_QUICK_FILTER
  // Only a type with a breakdown endpoint can drive the table. A URL saved while the mix cards were
  // still selectable would otherwise leave it with nothing to fetch and no way back.
  const selectedVarianceType = search.varianceType ?? DEFAULT_VARIANCE_TYPE
  const varianceType = isDrillableVarianceType(selectedVarianceType)
    ? selectedVarianceType
    : DEFAULT_VARIANCE_TYPE
  // Which KPI every dollar figure is read against. `/totals` carries both, so the cards switch without a
  // refetch; the grid is denominated in one, so switching it does refetch.
  const measure = search.measure ?? DEFAULT_VARIANCE_MEASURE

  // What narrows the query. There is no filter panel on this page: the quick-filter bar owns a set of the
  // scope params and the rest are read straight from the URL, so a hand-built link still scopes.
  const appliedScope = resolveVarianceScope(search, DEFAULT_VARIANCE_QUICK_FILTER)
  const productCategory = appliedScope.productCategory

  // The endpoints always report CAD, so the rate picked in the header is a pure display conversion: it
  // stays out of `appliedScope` and nothing below refetches when it changes.
  const { conversion } = useCrtCurrencySync({
    search,
    navigate: (update) => navigate({ search: update, replace: true }),
  })

  const {
    data: totals,
    isPending: isTotalsPending,
    error: totalsError,
  } = useVarianceTotals(comparison, appliedScope)

  const {
    data: breakdown,
    isPending: isBreakdownPending,
    isPlaceholderData: isBreakdownStale,
    error: breakdownError,
  } = useVarianceBreakdown(comparison && { comparison, scope: appliedScope, varianceType, measure })

  // The grid reports its own failure through the table's error state, but the totals cards have no error
  // state of their own and nothing catches a failed query globally — so a toast is the only signal that
  // the row of cards is showing nothing because the request failed rather than because there is nothing.
  //
  // A comparison the endpoint refused says *why* in a custom issue, which is worth reading: the Compare
  // button blocks all of those, so a request that carries one came from a hand-built URL and the reader
  // has no other way to find out what is wrong with it.
  useEffect(() => {
    if (!totalsError) return

    const fallbackDescription = t('errors.somethingWentWrong', { ns: 'common' })
    const [issue] = extractCustomZodIssues(totalsError)

    toast.error(
      issue
        ? getTranslatedCustomIssueDescription(issue, t, {
            ns: 'crt',
            keyPrefix: 'varianceAnalysis',
            fallbackDescription,
          })
        : fallbackDescription,
    )
  }, [totalsError, t])

  const handleComparisonChange = (patch: ComparisonPatch) => {
    navigate({ search: { ...search, ...patch } })
  }

  // A preset is only a named set of scope params, so selecting one writes them and lets everything
  // downstream read the URL as it always does. Every param a preset can write is cleared first: a preset
  // that does not carry a key means "not scoped by it", not "keep whatever the last one set".
  const handleQuickFilterSelect = (nextQuickFilter: VarianceQuickFilter) => {
    navigate({
      search: {
        ...search,
        quickFilter: nextQuickFilter,
        ...Object.fromEntries(QUICK_FILTER_SCOPE_KEYS.map((key) => [key, undefined])),
        ...(VARIANCE_QUICK_FILTER_SLICES[nextQuickFilter as VarianceQuickFilter] ?? {}),
      },
    })
  }

  // Switching type switches which breakdown endpoint answers, so the row set changes.
  const handleVarianceTypeSelect = (nextVarianceType: SelectableVarianceType) => {
    navigate({ search: { ...search, varianceType: nextVarianceType } })
  }

  return (
    <PageContainer maxWidth="wide">
      <PageHeader>{t('varianceAnalysis.pageHeader')}</PageHeader>

      <ComparisonConfigurator
        search={search}
        onSearchChange={handleComparisonChange}
        isEditOnly
        hasMeasureSwitch
      />

      {comparison ? (
        <VStack gap="lg" align="stretch">
          <QuickFilterBar selected={quickFilter} onSelect={handleQuickFilterSelect} />

          {/* TODO: there should be a loading state for those cards and an error state */}
          <VarianceTotalsCards
            totals={totals}
            isPending={isTotalsPending}
            selected={varianceType}
            onSelect={handleVarianceTypeSelect}
            measure={measure}
            currency={conversion}
          />

          <VarianceZeroNotes measure={measure} />

          <Card className="w-full">
            <CardHeader>
              <CardTitle>
                {' '}
                {t(
                  VARIANCE_TYPE_UNITS[varianceType] === VARIANCE_UNITS.UNITS
                    ? 'varianceAnalysis.heatmap.headerUnmeasured'
                    : 'varianceAnalysis.heatmap.header',
                  {
                    measure: t(`varianceAnalysis.measure.${measure}`),
                    varianceType: t(`varianceAnalysis.varianceType.${varianceType}`),
                  },
                )}
              </CardTitle>
              <CardDescription>{t('varianceAnalysis.heatmap.caption')}</CardDescription>
            </CardHeader>
            <CardContent>
              {/* The whole dataset. The grid renders every row rather than paging: a heat map is read
                  as a shape, and a page boundary would cut it in half. */}
              <VarianceHeatmap
                rows={breakdown?.results}
                productCategory={productCategory}
                varianceType={varianceType}
                currency={conversion}
                isPending={isBreakdownPending}
                isRefreshing={isBreakdownStale}
                error={breakdownError}
                // Clicking a cell drills into the cost types behind it, and the drill has to carry the
                // whole comparison — the two sides, the scope and the currency — so the link is built
                // from the page's own search rather than from the cell alone.
                search={search}
              />
            </CardContent>
          </Card>
        </VStack>
      ) : (
        <Card className="w-full">
          <CardContent className="py-16">
            <Text tone="muted" align="center">
              {t('varianceAnalysis.emptyState')}
            </Text>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  )
}
